import React, { useState, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  CircularProgress,
  LinearProgress,
  Chip,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  CloudUpload as CloudUploadIcon,
  Download as DownloadIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { importSurveysFromExcel } from '../services/api';

interface ImportResult {
  total_rows: number;
  imported: number;
  failed: number;
  errors: {
    row: number;
    error: string;
  }[];
}

const ImportSurveysPage: React.FC = () => {
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const [error, setError] = useState<string>('');
  const [dragActive, setDragActive] = useState(false);

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileSelection(e.dataTransfer.files[0]);
    }
  };

  const handleFileSelection = (file: File) => {
    // Validate file type
    const validTypes = [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-excel',
    ];
    
    if (!validTypes.includes(file.type) && !file.name.endsWith('.xlsx')) {
      setError(t('invalidFileType'));
      return;
    }
    
    setSelectedFile(file);
    setError('');
    setImportResult(null);
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      handleFileSelection(e.target.files[0]);
    }
  };

  const handleImport = async () => {
    if (!selectedFile) {
      setError(t('noFileSelected'));
      return;
    }

    setImporting(true);
    setError('');
    setImportResult(null);

    try {
      const result = await importSurveysFromExcel(selectedFile);
      setImportResult(result);
      
      // Clear file selection on success
      if (result.imported > 0) {
        setSelectedFile(null);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      }
    } catch (err: any) {
      setError(err.message || t('importFailed'));
    } finally {
      setImporting(false);
    }
  };

  const handleDownloadTemplate = () => {
    // Download template from backend
    const link = document.createElement('a');
    link.href = '/media/seed_survey_template.xlsx';
    link.download = 'seed_survey_template.xlsx';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h4" gutterBottom>
            {t('importSurveys')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('importSurveysDescription')}
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<DownloadIcon />}
          onClick={handleDownloadTemplate}
        >
          {t('downloadTemplate')}
        </Button>
      </Box>

      {/* Upload Area */}
      <Paper
        sx={{
          p: 4,
          mb: 3,
          border: dragActive ? '2px dashed #1976d2' : '2px dashed #ccc',
          backgroundColor: dragActive ? 'rgba(25, 118, 210, 0.05)' : 'background.paper',
          textAlign: 'center',
          cursor: 'pointer',
          transition: 'all 0.3s ease',
          '&:hover': {
            borderColor: '#1976d2',
            backgroundColor: 'rgba(25, 118, 210, 0.02)',
          },
        }}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept=".xlsx,.xls"
          style={{ display: 'none' }}
          onChange={handleFileInputChange}
        />
        
        <CloudUploadIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
        
        <Typography variant="h6" gutterBottom>
          {t('dragDropFile')}
        </Typography>
        
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t('orClickToSelect')}
        </Typography>
        
        {selectedFile && (
          <Chip
            label={selectedFile.name}
            color="primary"
            sx={{ mt: 2 }}
            onDelete={() => {
              setSelectedFile(null);
              if (fileInputRef.current) {
                fileInputRef.current.value = '';
              }
            }}
          />
        )}
      </Paper>

      {/* Error Display */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Import Button */}
      {selectedFile && !importing && !importResult && (
        <Box sx={{ mb: 3, textAlign: 'center' }}>
          <Button
            variant="contained"
            size="large"
            startIcon={<CloudUploadIcon />}
            onClick={handleImport}
          >
            {t('importNow')}
          </Button>
        </Box>
      )}

      {/* Progress */}
      {importing && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <CircularProgress size={24} sx={{ mr: 2 }} />
            <Typography variant="h6">{t('importing')}</Typography>
          </Box>
          <LinearProgress />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            {t('pleaseWait')}
          </Typography>
        </Paper>
      )}

      {/* Results */}
      {importResult && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            {t('importResults')}
          </Typography>

          <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
            <Chip
              icon={<InfoIcon />}
              label={`${t('totalRows')}: ${importResult.total_rows}`}
              color="info"
              variant="outlined"
            />
            <Chip
              icon={<CheckCircleIcon />}
              label={`${t('imported')}: ${importResult.imported}`}
              color="success"
              variant="outlined"
            />
            <Chip
              icon={<ErrorIcon />}
              label={`${t('failed')}: ${importResult.failed}`}
              color="error"
              variant="outlined"
            />
          </Box>

          {importResult.imported > 0 && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {t('importSuccessMessage', { count: importResult.imported })}
            </Alert>
          )}

          {/* Error Table */}
          {importResult.errors.length > 0 && (
            <>
              <Typography variant="subtitle1" gutterBottom sx={{ mt: 3 }}>
                {t('errors')} ({importResult.errors.length})
              </Typography>
              
              <TableContainer sx={{ maxHeight: 400 }}>
                <Table stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('rowNumber')}</TableCell>
                      <TableCell>{t('errorMessage')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {importResult.errors.map((error, index) => (
                      <TableRow key={index} hover>
                        <TableCell>
                          <Chip label={error.row} size="small" color="error" />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="error">
                            {error.error}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}

          {/* Try Again Button */}
          {importResult.failed > 0 && (
            <Box sx={{ mt: 3, textAlign: 'center' }}>
              <Button
                variant="outlined"
                onClick={() => {
                  setImportResult(null);
                  setSelectedFile(null);
                  if (fileInputRef.current) {
                    fileInputRef.current.value = '';
                  }
                }}
              >
                {t('tryAgain')}
              </Button>
            </Box>
          )}
        </Paper>
      )}

      {/* Instructions */}
      <Paper sx={{ p: 3, bgcolor: 'info.light', color: 'info.contrastText' }}>
        <Typography variant="h6" gutterBottom>
          {t('instructions')}
        </Typography>
        <Typography variant="body2" component="div">
          <ol style={{ marginTop: 0, paddingLeft: '20px' }}>
            <li>{t('instructionStep1')}</li>
            <li>{t('instructionStep2')}</li>
            <li>{t('instructionStep3')}</li>
            <li>{t('instructionStep4')}</li>
          </ol>
        </Typography>
        
        <Alert severity="warning" sx={{ mt: 2, bgcolor: 'warning.light' }}>
          <Typography variant="body2">
            <strong>{t('note')}:</strong> {t('requiredFieldsNote')}
          </Typography>
        </Alert>
      </Paper>
    </Box>
  );
};

export default ImportSurveysPage;
